import { ChangeEvent, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { SingleOptTimeRangePicker } from "components/date/SingleOptTimePeriodPicker"
import { DropdownMenu } from "components/inputs/DropdownMenu"
import { ModalDeclineAction } from "components/modals/ModalDeclineAction"
import { ModalKYCFiles } from "components/modals/ModalKYCFiles"
import { TopBar } from "components/navigation/TopBar"

import { useProps } from "contexts/PropsContext"

import { tenantOptions } from "constants/bonus"
import { tableKYCFilesColumns } from "constants/kyc-files"

import { kycService } from "services/kyc/kyc.service"

import { ModalConfirmAction } from "../components/modals/ModalConfirmAction"
import { Table } from "../components/tables/Table"

enum Tabs {
  PENDING = "PENDING",
  COMPLETED = "COMPLETED",
  DECLINED = "DECLINED"
}

export const KYCFiles = () => {
  const [activeTab, setActiveTab] = useState<Tabs>(Tabs.PENDING)
  const { t }: { t: any } = useTranslation(["translation"], { useSuspense: false })
  const [editRow, setEditRow] = useState<any>(null)
  const [openEditModal, setOpenEditModal] = useState<boolean>(false)
  const [declineRow, setDeclineRow] = useState<any>(null)
  const [approveRow, setApproveRow] = useState<any>(null)
  const [tableUpdatedAt, setTableUpdatedAt] = useState<Date | number>(Date.now())
  const [timeframe, setTimeframe] = useState<(Date | null)[]>([])
  const [tenant, setTenant] = useState<string>("")
  const { onKYCFilesCount } = useProps()

  const handleChangeTimeFrame = (dates: (Date | null)[]) => setTimeframe(dates)

  const editRowHandler = (): void => {
    setEditRow(null)
    setOpenEditModal(false)
  }

  const onOpenApproveRow = (row: any): void => {
    setApproveRow(row)
  }

  const approveRowHandler = (): void => {
    setApproveRow(null)
    setTableUpdatedAt(Date.now())
  }

  const onApprove = async (tx: any): Promise<void> => {
    if (!tx?.id) return

    try {
      const response = await kycService.updateKYCFilesStatus(tx.id, Tabs.COMPLETED)
      if (response) {
        // @ts-ignore
        onKYCFilesCount()
        toast.success(t("kyc.successAction", { actionText: "Approved" }))
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed")
    }
  }

  const onOpenDeclineRow = (row: any): void => {
    setDeclineRow(row)
  }

  const declineRowHandler = (): void => {
    setDeclineRow(null)
    setTableUpdatedAt(Date.now())
  }

  const onDecline = async (rowId: string, reason: string): Promise<void> => {
    const response = await kycService.updateKYCFilesStatus(rowId, Tabs.DECLINED, reason)
    if (response) {
      // @ts-ignore
      onKYCFilesCount()
      toast.success(t("kyc.successAction", { actionText: t("common.declined") }))
    }
  }

  const actions = [
    {
      name: t("common.decline"),
      condition: (row: any) => [Tabs.PENDING].includes(row?.status),
      handler: onOpenDeclineRow,
      buttonColor: "btn-secondary"
    },
    {
      name: "Approve",
      condition: (row: any) => [Tabs.PENDING].includes(row?.status),
      handler: onOpenApproveRow
    }
  ]

  const onRowClick = (row: any): void => {
    setEditRow(row)
    setOpenEditModal(true)
  }

  return (
    <div className="w-full p-8 pt-0">
      <TopBar title={t("pages.kycFiles.title")} />
      <div className="w-full grid grid-col-1 gap-8 py-8">
        <div className="flex items-center justify-center">
          <div className="tabs gap-2">
            {Object.keys(Tabs).map((tab) => (
              <a
                key={`tab-${tab}`}
                onClick={() => setActiveTab(tab as Tabs)}
                className={clsx(
                  "flex cursor-pointer items-center text-left py-3 rounded-lg px-4 text-base border text-[rgba(6,7,21,1)] dark:text-white border-[rgba(132,_0,_168,_1)] dark:border-transparent transition-all dark:bg-[#0C0C1A] hover:border-[rgba(255,255,255,0.1)]",
                  {
                    "transition-all text-white bg-[rgba(132,_0,_168,_1)] dark:bg-[rgba(132,_0,_168,_1)]": activeTab === tab,
                    "hover:border-[rgba(132,_0,_168,_0.8)] hover:dark:border-[rgba(132,_0,_168,_0.8)]  hover:bg-[rgba(169,78,206,0.2)] dark:hover:bg-[rgba(169,78,206,0.15)]":
                      activeTab !== tab
                  }
                )}
              >
                {tab}
              </a>
            ))}
          </div>
        </div>

        <div className="flex flex-row gap-8 max-w-lg z-20">
          <SingleOptTimeRangePicker onTimeframeChange={handleChangeTimeFrame} noLabel />
          <DropdownMenu
            placeholder={t("form.allTenants")}
            options={tenantOptions}
            value={tenant}
            onChange={(ev: ChangeEvent<HTMLSelectElement>) => setTenant(ev.target.value)}
            rounded
          />
        </div>

        <Table
          title={t("kyc.tableTitle", { tab: activeTab })}
          handler={kycService.getKYCFilesList}
          columns={tableKYCFilesColumns}
          actions={actions}
          updatedAt={tableUpdatedAt}
          filters={{
            status: activeTab,
            createdAt: timeframe
          }}
          onRowClick={onRowClick}
        />

        <ModalConfirmAction
          data={approveRow}
          handler={approveRowHandler}
          text={t("modal.confirmKycFiles", {
            actionText: "approve",
            user: approveRow?.user.username
          })}
          actionText={t("common.approve")}
          actionHandler={onApprove}
        />
        <ModalDeclineAction
          data={declineRow}
          handler={declineRowHandler}
          text={t("modal.confirmKycFiles", {
            actionText: "decline",
            user: declineRow?.user.username
          })}
          actionText={t("common.decline")}
          actionHandler={onDecline}
        />
        <ModalKYCFiles
          open={openEditModal}
          data={editRow?.user_id}
          handler={editRowHandler}
          approveHandler={onOpenApproveRow}
          declineHandler={onOpenDeclineRow}
        />
      </div>
    </div>
  )
}
